import React, { useMemo } from 'react';
import { Trans } from '@lingui/macro';
import ActionBlock from 'components/base/actionblock/actionblock';
import { ButtonPrint } from 'components/base/print/ButtonPrint';
import PageStandard from 'components/modules/pagestandard/pagestandard';
import { ExportReportButton } from 'components/modules/report/actions/ExportReportButton';
import { SaveReportModalButtons } from 'components/modules/report/actions/SaveReportModal';
import { reportFilterAreas } from 'components/modules/report/filter/fields/reportFilterAreas';
import { ReportFiltersBlock } from 'components/modules/report/filter/ReportFiltersBlock';
import {
  DEFAULT_SHOW,
  ReportColumn,
  ReportFilter,
  ReportViewProps,
} from 'components/modules/report/report-types';
import { ReportTable } from 'components/modules/report/table/ReportTable';
import { Model } from 'util/backendapi/models/api.interfaces';
import {
  END_OF_DAY,
  formatDateForDisplay,
  formatDatetimeForDisplayAsDate,
  START_OF_DAY,
} from 'util/dates';
import { NO_TIMEZONE } from 'components/modules/report/filter/fields/FilterDate';
import { useRouteComponentProps } from 'util/hooks';
import { reportFilterDateNoMenu } from 'components/modules/report/filter/fields/FilterDateNoMenu';
import { reportFilterObservationPoints } from 'components/modules/report/filter/fields/reportFilterObservationPoints';
import { DMSLink } from 'components/base/link/DMSLink';
import { ReportParamsTypes } from 'server/ssr.types';
import { MessageDescriptor } from '@lingui/core';
import Loading from 'components/base/loading/loading';
import { AlertDanger } from 'components/base/alert/alert';

export const observationPointTimeDependentFieldListFilters: ReportFilter[] = [
  reportFilterObservationPoints('observation_point'),
  reportFilterAreas('observation_point__site__area'),
  reportFilterDateNoMenu(
    'start-date',
    'start_datetime__gte',
    <Trans>Start date</Trans>,
    NO_TIMEZONE,
    START_OF_DAY
  ),
  reportFilterDateNoMenu(
    'end-date',
    'start_datetime__lte',
    <Trans>End date</Trans>,
    NO_TIMEZONE,
    END_OF_DAY
  ),
];

export const observationPointTimeDependentFieldListColumns = (extraProps?: {
  valueOptions?: Record<string, string>;
}): ReportColumn<Model.ReportsObservationPointTimeDependentField>[] => {
  return [
    {
      label: <Trans>Observation&nbsp;point</Trans>,
      name: 'observation-point',
      visibility: DEFAULT_SHOW,
      backendFieldName: 'observation_point__code',
      accessor: ({ observation_point__code: code }) => (
        <DMSLink to={`/observation-point/${code}`}>{code}</DMSLink>
      ),
    },
    {
      label: <Trans>Area</Trans>,
      name: 'area',
      visibility: DEFAULT_SHOW,
      backendFieldName: 'observation_point__site__area__code',
      additionalFields: ['observation_point__site__area__name'],
      accessor: ({
        observation_point__site__area__code: code,
        observation_point__site__area__name: name,
      }) => `${code} - ${name}`,
    },
    {
      label: <Trans>Change&nbsp;date</Trans>,
      name: 'start-datetime',
      visibility: DEFAULT_SHOW,
      backendFieldName: 'start_datetime',
      additionalFields: ['observation_point__site__area__time_zone__name'],
      accessor: ({
        start_datetime,
        observation_point__site__area__time_zone__name: time_zone,
      }) =>
        start_datetime &&
        formatDatetimeForDisplayAsDate(start_datetime, time_zone),
    },
    {
      label: <Trans>Value</Trans>,
      name: 'value',
      visibility: DEFAULT_SHOW,
      accessor: ({ value }) => extraProps?.valueOptions?.[value] ?? value,
    },
    {
      label: <Trans>Reason for change</Trans>,
      name: 'change_reason',
      visibility: DEFAULT_SHOW,
      accessor: ({ change_reason }) => change_reason,
    },
  ];
};

export function ObservationPointTimeDependentFieldListView(props: {
  reportProps: ReportViewProps<Model.ReportsObservationPointTimeDependentField>;
  header: React.ReactNode;
  reportUrl: ReportParamsTypes['type'];
  fileNameBase: MessageDescriptor;
  valueOptions?: Record<string, string>;
  isLoading?: boolean;
  isError?: boolean;
  errorMessage?: string;
}) {
  const {
    header,
    reportUrl,
    fileNameBase,
    valueOptions,
    isLoading,
    isError,
    errorMessage,
    reportProps,
  } = props;
  const filters = observationPointTimeDependentFieldListFilters;
  const columns = useMemo(
    () =>
      observationPointTimeDependentFieldListColumns({
        valueOptions,
      }),
    [valueOptions]
  );
  const routeProps = useRouteComponentProps();
  let pdfExportFilterMessage = null;

  if (reportProps.isExportMode) {
    const curFilters = new URLSearchParams(routeProps.location.search);
    const startDate = curFilters.get('start-date');
    const endDate = curFilters.get('end-date');
    pdfExportFilterMessage = (
      <div className="ssr-active-filters-block">
        <Trans>
          Data between {startDate ? formatDateForDisplay(startDate) : '-'} and{' '}
          {endDate ? formatDateForDisplay(endDate) : '-'}
        </Trans>
      </div>
    );
  }

  if (isLoading || isError) {
    return (
      <PageStandard name="obs-point-time-dependent-field" header={header}>
        {isLoading ? <Loading /> : <AlertDanger>{errorMessage}</AlertDanger>}
      </PageStandard>
    );
  }

  return (
    <PageStandard name="obs-point-time-dependent-field" header={header}>
      {pdfExportFilterMessage ? (
        pdfExportFilterMessage
      ) : (
        <div className="page-content-header-filters-actions">
          <ReportFiltersBlock
            filtersBackend={reportProps.reportInfo?.filters}
            filtersFrontend={filters}
            isExportMode={reportProps.isExportMode}
          />
        </div>
      )}
      <div className="filtered-table-wrapper">
        <ActionBlock className="text-right">
          <SaveReportModalButtons
            columnsFrontend={columns}
            filtersFrontend={filters}
            reportInfo={reportProps.reportInfo}
          />
          <ButtonPrint />
          <ExportReportButton
            fileNameBase={fileNameBase}
            reportUrl={reportUrl}
            columns={columns}
            filters={filters}
            pdfExtraParams={{
              valueOptions: props.valueOptions,
            }}
          />
        </ActionBlock>
        <ReportTable<Model.ReportsObservationPointTimeDependentField>
          {...reportProps}
          columns={columns}
          filters={filters}
        />
      </div>
    </PageStandard>
  );
}
